import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown, faChevronRight, faRocket, faBullseye, faBroadcastTower,
  faBox, faProjectDiagram, faServer, faTerminal, faBolt, faKey, faNetworkWired,
  faShieldAlt, faImage, faClock, faFolder, faGlobe
} from '@fortawesome/free-solid-svg-icons';

const Section = ({ title, icon, children, defaultOpen = false }) => {
  const { darkMode } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-lg border mb-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-2 px-4 py-3 text-left text-sm font-medium ${darkMode ? 'text-gray-200 hover:bg-gray-800' : 'text-gray-800 hover:bg-gray-50'}`}
      >
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="text-[10px] w-3" />
        {icon && <FontAwesomeIcon icon={icon} className="text-blue-400 w-4" />}
        {title}
      </button>
      {open && (
        <div className={`px-4 pb-4 text-xs leading-relaxed space-y-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {children}
        </div>
      )}
    </div>
  );
};

const Code = ({ children }) => {
  const { darkMode } = useTheme();
  return <code className={`px-1.5 py-0.5 rounded text-[11px] ${darkMode ? 'bg-gray-800 text-green-400' : 'bg-gray-100 text-green-700'}`}>{children}</code>;
};

const H = ({ children }) => {
  const { darkMode } = useTheme();
  return <h5 className={`text-xs font-semibold mt-3 mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{children}</h5>;
};

const C2Help = () => {
  const { darkMode } = useTheme();
  const cardClass = `text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`;

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-1" style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}>
      <div className="mb-4">
        <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          📖 C2 Console — Reference Guide
        </h3>
        <p className={cardClass}>
          Complete reference for PurpleHQ's command & control framework. Covers setup, agent deployment, infrastructure, and all tab functions.
        </p>
      </div>

      {/* QUICK START */}
      <Section title="Quick Start — First Agent in 5 Minutes" icon={faRocket} defaultOpen={true}>
        <ol className="list-decimal ml-4 space-y-1.5">
          <li><strong>Listeners tab</strong> → create an HTTPS listener on port 5000 (auto-created on build if none exists)</li>
          <li><strong>Payloads tab</strong> → click <em>Build Agent Payloads</em>, set name + C2 URL, choose modules (safe = no AV flags)</li>
          <li>Wait for build (~30s), then <strong>download</strong> the binary for your target platform</li>
          <li>Transfer binary to the target machine and execute it</li>
          <li><strong>Beacons tab</strong> → new beacon appears, click <em>Approve</em> to activate it</li>
          <li><strong>Terminal tab</strong> → run commands: <Code>shell whoami</Code>, <Code>screenshot</Code>, <Code>process_list</Code></li>
        </ol>
        <H>Typical Setup Order</H>
        <p><Code>Servers</Code> → <Code>Listeners</Code> → <Code>Redirectors</Code> (optional) → <Code>Payloads</Code> → deploy → <Code>Beacons</Code> → <Code>Terminal</Code></p>
      </Section>

      {/* ARCHITECTURE */}
      <Section title="Architecture Overview" icon={faShieldAlt}>
        <H>Components</H>
        <ul className="list-disc ml-4 space-y-1">
          <li><strong>C2 Server</strong> — PurpleHQ backend (Node.js/Express). Receives beacon check-ins, dispatches tasks, stores results.</li>
          <li><strong>Listener</strong> — HTTP/HTTPS endpoint on the C2 server that agents connect to. Each protocol type needs one listener.</li>
          <li><strong>Redirector</strong> — (Optional) Intermediate server that forwards traffic to the C2. Hides the real C2 IP. Deployed via SSH to VPS.</li>
          <li><strong>Agent/Beacon</strong> — Go binary running on the target. Periodically checks in, receives tasks, sends results back.</li>
          <li><strong>Stager</strong> — Tiny first-stage binary that downloads and runs the full agent. Smaller footprint for initial access.</li>
        </ul>
        <H>Communication Flow</H>
        <p><Code>Agent</Code> → (optional) <Code>Redirector(s)</Code> → <Code>Listener</Code> → <Code>C2 Server</Code></p>
        <p>All traffic is encrypted with AES-256-GCM + HMAC-SHA256. First check-in sends encryption keys; all subsequent messages are encrypted.</p>
        <H>Agent Check-In Cycle</H>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>Agent sleeps for <Code>sleepInterval ± jitter%</Code></li>
          <li>Sends encrypted POST to <Code>/api/c2/check-in</Code></li>
          <li>Server responds with pending tasks (or empty)</li>
          <li>Agent executes tasks, sends results on next check-in</li>
        </ol>
      </Section>

      {/* TAB REFERENCE */}
      <Section title="Tabs Reference" icon={faBolt}>
        <div className="space-y-3">
          <div>
            <H>📊 Overview</H>
            <p>Dashboard with live stats: active beacons, pending approvals, total tasks, inactive beacons. Shows recent activity log and beacon list.</p>
          </div>
          <div>
            <H>🎯 Beacons</H>
            <p>Lists all agents that have checked in. New beacons require <strong>approval</strong> before you can send them tasks.
            Each beacon shows: hostname, user, OS, IP, last check-in time, sleep interval.
            Right-click a beacon for context menu: approve, configure sleep/jitter, kill, delete.</p>
            <p><strong>Agent Downloads</strong> section at the bottom shows pre-built payloads available for download.</p>
          </div>
          <div>
            <H>⚡ Tasks</H>
            <p>Full task history across all beacons. Filter by status (pending/running/completed/failed/cancelled). Shows command, output, timing. Retry or cancel tasks from here.</p>
          </div>
          <div>
            <H>🔑 Credentials</H>
            <p>Credential vault — stores credentials harvested by agents (from mimikatz, LSASS dumps, Kerberoast, etc.). Encrypted at rest. Export for reporting.</p>
          </div>
          <div>
            <H>📁 Files</H>
            <p>Files downloaded from targets. Preview text/images, download originals. Automatically populated by <Code>download</Code> command results.</p>
          </div>
          <div>
            <H>📸 Screenshots</H>
            <p>Gallery of screenshots captured by agents. Multi-monitor support. Click to enlarge, download originals.</p>
          </div>
          <div>
            <H>⏱ Jobs</H>
            <p>Recurring/scheduled tasks (e.g., "take screenshot every 5 minutes"). Create jobs that automatically queue tasks at intervals.</p>
          </div>
          <div>
            <H>🌐 Port Scanner</H>
            <p>Remote port scanning through agents. Scan internal networks the agent can reach. Scans common ports (21, 22, 80, 443, 445, 3389, etc.) or custom ranges.</p>
          </div>
          <div>
            <H>📡 Listeners</H>
            <p>Manage C2 listeners. Types: HTTP, HTTPS, DNS, SMB. Each listener binds to a port and protocol.
            Start/stop listeners, view connection logs. At least one listener must be running for agents to check in.</p>
          </div>
          <div>
            <H>💻 Terminal</H>
            <p>Interactive command terminal for selected beacon. Type commands directly — similar to a Cobalt Strike/Sliver console.
            Supports all agent commands: <Code>shell</Code>, <Code>powershell</Code>, <Code>download</Code>, <Code>upload</Code>, <Code>screenshot</Code>, etc.</p>
          </div>
          <div>
            <H>📦 Payloads</H>
            <p>Build and manage agent binaries. See "Building Agents" section below for module details. Lists all builds with download links, build configs, and SHA256 hashes.</p>
          </div>
          <div>
            <H>🔀 Redirectors</H>
            <p>Manage traffic redirectors. Deploy to VPS via SSH — configures socat/nginx to forward traffic to the C2. Chain multiple redirectors for resilience.
            Test connection, view logs, restart remotely.</p>
          </div>
          <div>
            <H>🏗️ Infrastructure</H>
            <p>Visual drag-and-drop infrastructure designer. Place agents, redirectors, listeners, and C2 servers on a canvas. Draw connections between them.
            Validates topology (no isolated nodes, proper chain), then deploys in the correct order. Uses D3.js force simulation for layout.</p>
          </div>
          <div>
            <H>🖥️ Servers</H>
            <p>Registry of VPS/cloud servers for deploying redirectors and listeners. Store SSH credentials, manage server inventory.</p>
          </div>
        </div>
      </Section>

      {/* BUILDING AGENTS */}
      <Section title="Building Agents — Modules & AV Detection" icon={faBox}>
        <H>Build Profiles</H>
        <p>The agent uses a modular build system. Each module is a Go build tag — when not selected, the code is <strong>not compiled</strong> into the binary at all.</p>
        <div className={`mt-2 rounded overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <th className="px-3 py-1.5 text-left">Module</th>
                <th className="px-3 py-1.5 text-left">Build Tag</th>
                <th className="px-3 py-1.5 text-left">Capabilities</th>
                <th className="px-3 py-1.5 text-center">AV Risk</th>
              </tr>
            </thead>
            <tbody className={darkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}>
              <tr><td className="px-3 py-1.5 font-medium">Base (always)</td><td className="px-3 py-1.5"><Code>—</Code></td><td className="px-3 py-1.5">Shell, file ops, screenshots, process list, network scan, sysinfo</td><td className="px-3 py-1.5 text-center">✅ Safe</td></tr>
              <tr><td className="px-3 py-1.5 font-medium">Evasion</td><td className="px-3 py-1.5"><Code>evasion</Code></td><td className="px-3 py-1.5">AMSI bypass (AmsiScanBuffer patch), ETW bypass (EtwEventWrite patch)</td><td className="px-3 py-1.5 text-center">⚠️ High</td></tr>
              <tr><td className="px-3 py-1.5 font-medium">Credentials</td><td className="px-3 py-1.5"><Code>credentials</Code></td><td className="px-3 py-1.5">Mimikatz, LSASS dump, DCSync, Kerberoast, Chrome/WiFi passwords</td><td className="px-3 py-1.5 text-center">⚠️ High</td></tr>
              <tr><td className="px-3 py-1.5 font-medium">Lateral</td><td className="px-3 py-1.5"><Code>lateral</Code></td><td className="px-3 py-1.5">PSExec, WMI exec, schtasks, service exec</td><td className="px-3 py-1.5 text-center">⚠️ High</td></tr>
              <tr><td className="px-3 py-1.5 font-medium">Injection</td><td className="px-3 py-1.5"><Code>injection</Code></td><td className="px-3 py-1.5">CreateRemoteThread, QueueUserAPC, NtQueueApcThread</td><td className="px-3 py-1.5 text-center">⚠️ High</td></tr>
              <tr><td className="px-3 py-1.5 font-medium">Keylogger</td><td className="px-3 py-1.5"><Code>keylogger</Code></td><td className="px-3 py-1.5">SetWindowsHookEx (Win), /dev/input (Linux)</td><td className="px-3 py-1.5 text-center">⚠️ Medium</td></tr>
            </tbody>
          </table>
        </div>
        <H>Why Does Defender Flag the Agent?</H>
        <p>When offensive modules are compiled in, the binary contains:</p>
        <ul className="list-disc ml-4 space-y-0.5">
          <li><strong>Signature strings</strong> — "AmsiScanBuffer", "comsvcs.dll,MiniDump", "lsass.exe" etc.</li>
          <li><strong>Known byte patterns</strong> — AMSI patch bytes (<Code>0xB8 0x00 0x00 0x00 0x00 0xC3</Code>)</li>
          <li><strong>Suspicious API imports</strong> — VirtualAllocEx + WriteProcessMemory + CreateRemoteThread (injection chain)</li>
        </ul>
        <p className="mt-1">A <strong>safe build</strong> (no modules selected) avoids all of these — it only contains basic shell/file/screenshot code that doesn't match any AV signatures.</p>
        <H>Recommended Approach</H>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>Deploy a <strong>safe build</strong> first — establish persistence without detection</li>
          <li>When you need offensive capabilities, send a full binary via <Code>upload</Code> + execute, OR use <Code>powershell</Code> to run tools in-memory</li>
          <li>Add Defender exclusions on authorized pentest machines before deploying offensive builds</li>
        </ol>
      </Section>

      {/* AGENT COMMANDS */}
      <Section title="Agent Commands Reference" icon={faTerminal}>
        <p>Send these via the Terminal tab or as tasks from the Beacons tab.</p>
        <div className={`mt-2 rounded overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <th className="px-3 py-1.5 text-left">Command</th>
                <th className="px-3 py-1.5 text-left">Module</th>
                <th className="px-3 py-1.5 text-left">Description</th>
              </tr>
            </thead>
            <tbody className={`${darkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}`}>
              <tr className={darkMode ? 'bg-gray-900/30' : 'bg-gray-50/50'}><td colSpan={3} className="px-3 py-1 font-medium">Base Commands</td></tr>
              <tr><td className="px-3 py-1"><Code>shell &lt;cmd&gt;</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Run a shell command (cmd.exe / /bin/sh)</td></tr>
              <tr><td className="px-3 py-1"><Code>powershell &lt;cmd&gt;</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Run PowerShell command (hidden window)</td></tr>
              <tr><td className="px-3 py-1"><Code>ls [path]</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">List directory contents</td></tr>
              <tr><td className="px-3 py-1"><Code>cat &lt;file&gt;</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Read file contents</td></tr>
              <tr><td className="px-3 py-1"><Code>download &lt;path&gt;</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Download file from target (max 50MB)</td></tr>
              <tr><td className="px-3 py-1"><Code>upload &lt;path&gt;</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Upload file to target</td></tr>
              <tr><td className="px-3 py-1"><Code>screenshot</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Capture all monitors (PNG)</td></tr>
              <tr><td className="px-3 py-1"><Code>process_list</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">List running processes (PID, name, user, memory)</td></tr>
              <tr><td className="px-3 py-1"><Code>network_scan &lt;target&gt;</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">TCP port scan (e.g., 192.168.1.0/24)</td></tr>
              <tr><td className="px-3 py-1"><Code>sleep &lt;sec&gt;</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Change check-in interval</td></tr>
              <tr><td className="px-3 py-1"><Code>jitter &lt;%&gt;</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Change jitter percentage</td></tr>
              <tr><td className="px-3 py-1"><Code>kill</Code></td><td className="px-3 py-1">—</td><td className="px-3 py-1">Terminate the agent</td></tr>

              <tr className={darkMode ? 'bg-gray-900/30' : 'bg-gray-50/50'}><td colSpan={3} className="px-3 py-1 font-medium">⚠️ Evasion Module</td></tr>
              <tr><td className="px-3 py-1"><Code>patch_amsi</Code></td><td className="px-3 py-1">evasion</td><td className="px-3 py-1">Disable AMSI (PowerShell AV scanning)</td></tr>
              <tr><td className="px-3 py-1"><Code>patch_etw</Code></td><td className="px-3 py-1">evasion</td><td className="px-3 py-1">Disable ETW (EDR telemetry)</td></tr>
              <tr><td className="px-3 py-1"><Code>patch_both</Code></td><td className="px-3 py-1">evasion</td><td className="px-3 py-1">Patch both AMSI + ETW</td></tr>

              <tr className={darkMode ? 'bg-gray-900/30' : 'bg-gray-50/50'}><td colSpan={3} className="px-3 py-1 font-medium">⚠️ Credentials Module</td></tr>
              <tr><td className="px-3 py-1"><Code>mimikatz [module]</Code></td><td className="px-3 py-1">credentials</td><td className="px-3 py-1">Run mimikatz (default: sekurlsa::logonpasswords)</td></tr>
              <tr><td className="px-3 py-1"><Code>lsass_dump</Code></td><td className="px-3 py-1">credentials</td><td className="px-3 py-1">Dump LSASS process memory</td></tr>
              <tr><td className="px-3 py-1"><Code>dcsync &lt;user&gt;</Code></td><td className="px-3 py-1">credentials</td><td className="px-3 py-1">DCSync attack (requires DA rights)</td></tr>
              <tr><td className="px-3 py-1"><Code>kerberoast</Code></td><td className="px-3 py-1">credentials</td><td className="px-3 py-1">Enumerate SPNs for Kerberoasting</td></tr>

              <tr className={darkMode ? 'bg-gray-900/30' : 'bg-gray-50/50'}><td colSpan={3} className="px-3 py-1 font-medium">⚠️ Lateral Movement Module</td></tr>
              <tr><td className="px-3 py-1"><Code>psexec &lt;target&gt;</Code></td><td className="px-3 py-1">lateral</td><td className="px-3 py-1">Remote execution via SMB</td></tr>
              <tr><td className="px-3 py-1"><Code>wmi_exec &lt;target&gt;</Code></td><td className="px-3 py-1">lateral</td><td className="px-3 py-1">Remote execution via WMI</td></tr>
              <tr><td className="px-3 py-1"><Code>schtasks &lt;target&gt;</Code></td><td className="px-3 py-1">lateral</td><td className="px-3 py-1">Create & run remote scheduled task</td></tr>

              <tr className={darkMode ? 'bg-gray-900/30' : 'bg-gray-50/50'}><td colSpan={3} className="px-3 py-1 font-medium">⚠️ Keylogger Module</td></tr>
              <tr><td className="px-3 py-1"><Code>keylog_start</Code></td><td className="px-3 py-1">keylogger</td><td className="px-3 py-1">Start capturing keystrokes</td></tr>
              <tr><td className="px-3 py-1"><Code>keylog_stop</Code></td><td className="px-3 py-1">keylogger</td><td className="px-3 py-1">Stop keylogger and return captured data</td></tr>
            </tbody>
          </table>
        </div>
        <p className="mt-2">Commands requiring an uncompiled module return: <Code>module not compiled — rebuild agent with 'X' build tag</Code></p>
      </Section>

      {/* INFRASTRUCTURE */}
      <Section title="Infrastructure Setup" icon={faProjectDiagram}>
        <H>Minimal Setup (Direct Connection)</H>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>Register your C2 server in <strong>Servers</strong> tab (hostname, SSH credentials)</li>
          <li>Create a <strong>Listener</strong> (HTTPS on port 443 or 5000)</li>
          <li>Build agent with C2 URL pointing directly to the listener</li>
          <li>Deploy — agent connects directly to C2</li>
        </ol>
        <H>Redirector Chain (Recommended for Engagements)</H>
        <ol className="list-decimal ml-4 space-y-0.5">
          <li>Register C2 server + VPS(es) in <strong>Servers</strong> tab</li>
          <li>Create <strong>Redirectors</strong> — point them at the C2 server, deploy via SSH (socat/nginx)</li>
          <li>Create <strong>Listener</strong> on the C2</li>
          <li>Build agent — in route config, select the redirector chain. Agent connects to redirector → forwards to C2.</li>
          <li>If a redirector is burned, deploy a new one and update the route — C2 stays hidden.</li>
        </ol>
        <H>Infrastructure Builder (Visual)</H>
        <p>Drag-and-drop designer in the <strong>Infrastructure</strong> tab. Place nodes (Agent, Redirector, Listener, C2 Server), draw connections, then hit Deploy.
        The deployer validates the topology and executes in the correct order: C2 → Listeners → Redirectors → Agents.</p>
        <H>Failover / Resilience</H>
        <p>Build agents with multiple redirector URLs. The agent cycles through them on failure — strategies: <Code>sequential</Code>, <Code>random</Code>, <Code>health_based</Code> (tracks success rates and quarantines failing URLs).</p>
      </Section>

      {/* DNS C2 TRANSPORT */}
      <Section title="DNS C2 Transport — Setup & Usage" icon={faGlobe}>
        <p>DNS tunneling lets agents communicate over DNS TXT queries instead of HTTP/HTTPS. All data is encoded into subdomain labels,
        making C2 traffic look like ordinary DNS lookups. This is useful when HTTP/HTTPS egress is blocked but DNS resolution is allowed.</p>

        <H>When to Use DNS C2</H>
        <ul className="list-disc ml-4 space-y-0.5">
          <li>Target network blocks all HTTP(S) outbound traffic</li>
          <li>Only DNS (port 53) is allowed out — common in segmented/OT networks</li>
          <li>You want a low-and-slow covert channel that blends with normal DNS traffic</li>
          <li>As a backup transport when HTTP redirectors are burned</li>
        </ul>
        <p className="mt-1"><strong>Trade-offs:</strong> DNS is slower (~118 bytes per query vs full HTTP POST), does not support file exfiltration (screenshots, downloads), and requires DNS zone delegation.</p>

        <H>Prerequisites</H>
        <ul className="list-disc ml-4 space-y-0.5">
          <li>A <strong>domain you control</strong> (e.g., <Code>purplehq.ch</Code>) with access to its DNS management</li>
          <li>A <strong>server with a public IP</strong> that can bind UDP port 53 (your C2 server at <Code>185.145.148.203</Code>)</li>
          <li>Port 53/UDP open in the server's firewall</li>
        </ul>

        <H>Step 1 — Plan Your DNS Zone</H>
        <p>Pick a subdomain zone that will carry C2 traffic. All agent queries will be subdomains of this zone.</p>
        <div className={`mt-1 p-3 rounded font-mono text-[11px] leading-relaxed ${darkMode ? 'bg-gray-800 text-green-400' : 'bg-gray-100 text-green-700'}`}>
          <div className="text-gray-500 mb-1"># Example zone structure</div>
          <div>Zone:&nbsp;&nbsp;&nbsp;&nbsp; <strong>c2.purplehq.ch</strong></div>
          <div>C2 IP:&nbsp;&nbsp; <strong>185.145.148.203</strong></div>
          <div className="mt-1 text-gray-500"># Agent queries will look like:</div>
          <div>0.3.a1b2.JBSWY3DPEB.RWSC3LY.MFZWIZLE.f8c1.c2.purplehq.ch TXT?</div>
        </div>

        <H>Step 2 — Create DNS Zone Records</H>
        <p>In your domain's DNS management panel (e.g., Cloudflare, Infomaniak, Route53), create the following records:</p>

        <div className={`mt-1 rounded overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <th className="px-3 py-1.5 text-left">Record</th>
                <th className="px-3 py-1.5 text-left">Name</th>
                <th className="px-3 py-1.5 text-left">Value</th>
                <th className="px-3 py-1.5 text-left">Purpose</th>
              </tr>
            </thead>
            <tbody className={darkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}>
              <tr>
                <td className="px-3 py-1.5 font-medium">A</td>
                <td className="px-3 py-1.5"><Code>ns1.purplehq.ch</Code></td>
                <td className="px-3 py-1.5"><Code>185.145.148.203</Code></td>
                <td className="px-3 py-1.5">Points the nameserver hostname to your C2 server IP</td>
              </tr>
              <tr>
                <td className="px-3 py-1.5 font-medium">NS</td>
                <td className="px-3 py-1.5"><Code>c2.purplehq.ch</Code></td>
                <td className="px-3 py-1.5"><Code>ns1.purplehq.ch</Code></td>
                <td className="px-3 py-1.5">Delegates the <Code>c2</Code> subdomain zone to your C2 server</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className={`mt-2 p-2.5 rounded text-[11px] ${darkMode ? 'bg-blue-900/30 text-blue-300 border border-blue-800' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
          <strong>📌 How NS delegation works:</strong> The NS record tells the global DNS system: "for anything under <Code>c2.purplehq.ch</Code>, ask <Code>ns1.purplehq.ch</Code>."
          The A record for <Code>ns1</Code> resolves to your C2 server's IP.
          When the agent asks for <Code>xyz.c2.purplehq.ch</Code>, the query is routed to your C2, which parses the encoded data from the subdomain labels.
        </div>

        <div className={`mt-2 p-3 rounded font-mono text-[11px] leading-relaxed ${darkMode ? 'bg-gray-800 text-green-400' : 'bg-gray-100 text-green-700'}`}>
          <div className="text-gray-500 mb-1"># Zone file equivalent (BIND format)</div>
          <div>ns1&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; IN&nbsp;&nbsp; A&nbsp;&nbsp;&nbsp;&nbsp; 185.145.148.203</div>
          <div>c2&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; IN&nbsp;&nbsp; NS&nbsp;&nbsp;&nbsp; ns1.purplehq.ch.</div>
        </div>

        <H>Step 3 — Verify DNS Delegation</H>
        <p>Before creating the listener, verify that delegation is working:</p>
        <div className={`mt-1 p-3 rounded font-mono text-[11px] leading-relaxed ${darkMode ? 'bg-gray-800 text-green-400' : 'bg-gray-100 text-green-700'}`}>
          <div className="text-gray-500"># Check NS record</div>
          <div>$ dig NS c2.purplehq.ch +short</div>
          <div className="text-yellow-400">ns1.purplehq.ch.</div>
          <div className="mt-1 text-gray-500"># Check A record for nameserver</div>
          <div>$ dig A ns1.purplehq.ch +short</div>
          <div className="text-yellow-400">185.145.148.203</div>
          <div className="mt-1 text-gray-500"># Test that queries reach your server (after listener is running)</div>
          <div>$ dig TXT test.c2.purplehq.ch +short</div>
          <div className="text-yellow-400">"..." (or timeout if listener not yet running)</div>
        </div>
        <p className="mt-1">DNS propagation can take 5–60 minutes depending on your registrar. If using Cloudflare, ensure the NS record is <strong>DNS Only</strong> (grey cloud), not proxied.</p>

        <H>Step 4 — Create DNS Listener in PurpleHQ</H>
        <ol className="list-decimal ml-4 space-y-1">
          <li>Go to the <strong>Listeners</strong> tab</li>
          <li>Click <strong>Create Listener</strong></li>
          <li>Set <strong>Type</strong> to <Code>DNS</Code></li>
          <li>Set <strong>DNS Zone</strong> to your delegated zone: <Code>c2.purplehq.ch</Code></li>
          <li>Set <strong>Port</strong> to <Code>53</Code> (default, must match what the NS record points to)</li>
          <li>Set <strong>Bind Address</strong> to <Code>0.0.0.0</Code> (all interfaces)</li>
          <li>Click <strong>Save</strong> and then <strong>Start</strong> the listener</li>
        </ol>
        <div className={`mt-2 p-2.5 rounded text-[11px] ${darkMode ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-800' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
          <strong>⚠️ Port 53:</strong> Your C2 server must not have another DNS server (like systemd-resolved or dnsmasq) occupying port 53.
          On Ubuntu: <Code>sudo systemctl stop systemd-resolved && sudo systemctl disable systemd-resolved</Code>
          and set <Code>nameserver 8.8.8.8</Code> in <Code>/etc/resolv.conf</Code>.
        </div>

        <H>Step 5 — Build a DNS Agent</H>
        <ol className="list-decimal ml-4 space-y-1">
          <li>Go to <strong>Payloads</strong> tab → <strong>Build Agent Payloads</strong></li>
          <li>Set a name and select target platform</li>
          <li>Under <strong>Protocol</strong>, select <Code>DNS</Code></li>
          <li>The <strong>DNS Zone</strong> field auto-populates from your DNS listener config (e.g., <Code>c2.purplehq.ch</Code>)</li>
          <li>Optionally set a <strong>DNS Server</strong> (direct mode — see below)</li>
          <li>Select modules as needed (base is recommended — offensive modules work the same over DNS)</li>
          <li>Click <strong>Build</strong></li>
        </ol>

        <H>Step 6 — Deploy & Test</H>
        <ol className="list-decimal ml-4 space-y-1">
          <li>Transfer the built binary to the target machine</li>
          <li>Execute it — the agent starts sending DNS TXT queries to the zone</li>
          <li>In the <strong>Beacons</strong> tab, a new beacon appears with protocol <Code>dns</Code></li>
          <li><strong>Approve</strong> the beacon</li>
          <li>Use the <strong>Terminal</strong> tab to send commands — responses come back via DNS</li>
        </ol>

        <H>System Resolver vs Direct Mode</H>
        <div className={`mt-1 rounded overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <th className="px-3 py-1.5 text-left">Mode</th>
                <th className="px-3 py-1.5 text-left">DNS Server field</th>
                <th className="px-3 py-1.5 text-left">How it works</th>
                <th className="px-3 py-1.5 text-left">Stealth</th>
              </tr>
            </thead>
            <tbody className={darkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}>
              <tr>
                <td className="px-3 py-1.5 font-medium">System Resolver</td>
                <td className="px-3 py-1.5"><em>(empty)</em></td>
                <td className="px-3 py-1.5">Agent uses the target's configured DNS resolver (e.g., corporate DNS). Query flows: target → corporate DNS → recursive → authoritative (your C2).</td>
                <td className="px-3 py-1.5 text-center">✅ High</td>
              </tr>
              <tr>
                <td className="px-3 py-1.5 font-medium">Direct</td>
                <td className="px-3 py-1.5"><Code>185.145.148.203:53</Code></td>
                <td className="px-3 py-1.5">Agent sends DNS queries directly to the C2 server IP. Bypasses NS delegation — no zone setup needed.</td>
                <td className="px-3 py-1.5 text-center">⚠️ Low</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-1"><strong>Recommendation:</strong> Use <strong>System Resolver</strong> for real engagements (traffic blends with normal DNS).
        Use <strong>Direct</strong> for testing or when you can't control the domain's DNS records.</p>

        <H>DNS Protocol Details</H>
        <p>Understanding the wire format helps with debugging and detection engineering:</p>
        <ul className="list-disc ml-4 space-y-1">
          <li><strong>Encoding:</strong> Agent data is Base32-encoded (RFC 4648, uppercase, no padding) — DNS-safe and case-insensitive</li>
          <li><strong>Query format:</strong> <Code>&lt;seq&gt;.&lt;total&gt;.&lt;msgId&gt;.&lt;data1&gt;.&lt;data2&gt;.&lt;data3&gt;.&lt;nonce&gt;.&lt;zone&gt;</Code></li>
          <li><strong>Label limits:</strong> Each DNS label is max 63 chars → 3 data labels = ~118 bytes per query (after Base32 decoding)</li>
          <li><strong>Multi-query messages:</strong> Large payloads (registration, encrypted check-ins) are split across 3–6 queries, reassembled server-side by <Code>msgId</Code></li>
          <li><strong>Nonce:</strong> Random 4-char hex per query — prevents DNS caching (+ TTL=0 on responses)</li>
          <li><strong>Response:</strong> Intermediate chunks get <Code>"ACK"</Code> TXT; final chunk gets the full Base64-encoded JSON response</li>
          <li><strong>Encryption:</strong> Same AES-256-GCM + HMAC-SHA256 as HTTP — DNS transport is transparent to the crypto layer</li>
        </ul>
        <div className={`mt-2 p-3 rounded font-mono text-[11px] leading-relaxed ${darkMode ? 'bg-gray-800 text-green-400' : 'bg-gray-100 text-green-700'}`}>
          <div className="text-gray-500"># Example: 3-query check-in message</div>
          <div>Query 1: 0.3.a1b2c3d4.JBSWY3DPEB3W64TMMQQGM4TPMUQG433UMF2GS.MFZWIZLTOQQHAYLHMVZGK.NBUWYZLOMQQGQ33DMFZC.f8c1.c2.purplehq.ch TXT</div>
          <div className="text-gray-500">→ Response: "ACK"</div>
          <div className="mt-0.5">Query 2: 1.3.a1b2c3d4.A5SXQZJAON2HE2LOM4QHIZLTORSXE5BON2.GEZDGOBSMFZSA.pad.3af2.c2.purplehq.ch TXT</div>
          <div className="text-gray-500">→ Response: "ACK"</div>
          <div className="mt-0.5">Query 3: 2.3.a1b2c3d4.ENDMARKER.pad.pad.7b91.c2.purplehq.ch TXT</div>
          <div className="text-gray-500">→ Response: "eyJ0YXNrcyI6W10sInN..." (base64 JSON)</div>
        </div>

        <H>Limitations</H>
        <ul className="list-disc ml-4 space-y-0.5">
          <li><strong>File size limit: 1 MB</strong> — <Code>download</Code> and <Code>screenshot</Code> work over DNS but are capped at 1 MB per file. Files above the limit are skipped with a warning. Use small files to prove DNS exfiltration in reports.</li>
          <li><strong>Slower</strong> — A check-in takes 3–5 DNS queries; registration 4–6 queries. A 100 KB file ≈ 1,160 queries. Plan for minutes, not seconds.</li>
          <li><strong>DNS caching</strong> — Recursive resolvers may cache TXT records despite TTL=0. The random nonce per query mitigates this.</li>
          <li><strong>Query logging</strong> — Corporate DNS servers may log all queries. Long Base32-encoded subdomains are suspicious in log review.</li>
          <li><strong>UDP size</strong> — Very large task outputs may be truncated. Text-based results (shell output, process lists) work fine.</li>
        </ul>

        <H>Troubleshooting</H>
        <ul className="list-disc ml-4 space-y-1">
          <li><strong>Beacon not appearing?</strong>
            <ul className="list-disc ml-4 mt-0.5 space-y-0.5">
              <li>Check that the DNS listener is <strong>started</strong> (green status in Listeners tab)</li>
              <li>Verify port 53/UDP is open: <Code>sudo ss -ulnp | grep :53</Code></li>
              <li>Test from target: <Code>nslookup -type=TXT test.c2.purplehq.ch</Code></li>
              <li>Check PM2 logs: <Code>pm2 logs purpleteam-api | grep DNS</Code></li>
            </ul>
          </li>
          <li><strong>Port 53 already in use?</strong>
            <ul className="list-disc ml-4 mt-0.5 space-y-0.5">
              <li>systemd-resolved: <Code>sudo systemctl stop systemd-resolved</Code></li>
              <li>dnsmasq: <Code>sudo systemctl stop dnsmasq</Code></li>
              <li>Or use a non-standard port (e.g., 5353) with Direct mode only — NS delegation always expects port 53</li>
            </ul>
          </li>
          <li><strong>Queries not reaching the server?</strong>
            <ul className="list-disc ml-4 mt-0.5 space-y-0.5">
              <li>Check NS delegation: <Code>dig NS c2.purplehq.ch</Code> — should return <Code>ns1.purplehq.ch</Code></li>
              <li>Check glue record: <Code>dig A ns1.purplehq.ch</Code> — should return your C2 IP</li>
              <li>DNS propagation may take up to 60 minutes</li>
              <li>Cloudflare users: ensure NS record has <strong>grey cloud</strong> (DNS Only), not orange (Proxied)</li>
            </ul>
          </li>
          <li><strong>Agent exits with "DNS error"?</strong>
            <ul className="list-disc ml-4 mt-0.5 space-y-0.5">
              <li>In Direct mode, verify the DNS server IP is reachable from the target</li>
              <li>In System Resolver mode, ensure the target can resolve external domains at all: <Code>nslookup google.com</Code></li>
            </ul>
          </li>
        </ul>

        <H>Quick Reference — Comparison with HTTP</H>
        <div className={`mt-1 rounded overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <th className="px-3 py-1.5 text-left">Feature</th>
                <th className="px-3 py-1.5 text-left">HTTP/HTTPS</th>
                <th className="px-3 py-1.5 text-left">DNS</th>
              </tr>
            </thead>
            <tbody className={darkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}>
              <tr><td className="px-3 py-1 font-medium">Bandwidth</td><td className="px-3 py-1">Full HTTP body (MBs)</td><td className="px-3 py-1">~118 bytes/query</td></tr>
              <tr><td className="px-3 py-1 font-medium">File exfiltration</td><td className="px-3 py-1">✅ Yes (50MB max)</td><td className="px-3 py-1">✅ Yes (1MB max)</td></tr>
              <tr><td className="px-3 py-1 font-medium">Screenshots</td><td className="px-3 py-1">✅ Yes</td><td className="px-3 py-1">✅ Yes (if &lt; 1MB)</td></tr>
              <tr><td className="px-3 py-1 font-medium">Check-in queries</td><td className="px-3 py-1">1 HTTP POST</td><td className="px-3 py-1">3–5 DNS TXT queries</td></tr>
              <tr><td className="px-3 py-1 font-medium">Encryption</td><td className="px-3 py-1">AES-256-GCM + TLS</td><td className="px-3 py-1">AES-256-GCM (in DNS payload)</td></tr>
              <tr><td className="px-3 py-1 font-medium">Infrastructure</td><td className="px-3 py-1">Listener + optional redirector</td><td className="px-3 py-1">Listener + NS delegation</td></tr>
              <tr><td className="px-3 py-1 font-medium">Stealth</td><td className="px-3 py-1">Blends with web traffic</td><td className="px-3 py-1">Blends with DNS traffic</td></tr>
              <tr><td className="px-3 py-1 font-medium">Failover</td><td className="px-3 py-1">Multi-URL strategies</td><td className="px-3 py-1">Single zone (use Direct as backup)</td></tr>
              <tr><td className="px-3 py-1 font-medium">Works when HTTP blocked</td><td className="px-3 py-1">❌</td><td className="px-3 py-1">✅ Only needs DNS resolution</td></tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* SECURITY */}
      <Section title="Operational Security" icon={faShieldAlt}>
        <ul className="list-disc ml-4 space-y-1">
          <li><strong>Kill date</strong> — Always set to engagement end + buffer. Prevents rogue agents post-engagement.</li>
          <li><strong>Working hours</strong> — Restrict check-ins to business hours to blend with normal traffic.</li>
          <li><strong>Sleep + jitter</strong> — Higher sleep (300s+) with 30-50% jitter mimics human browsing patterns. Low sleep (5s) is fast but noisy.</li>
          <li><strong>Beacon approval</strong> — New agents require manual approval before accepting tasks. Prevents unauthorized agents from executing commands.</li>
          <li><strong>Encryption</strong> — All C2 traffic uses AES-256-GCM + HMAC-SHA256. First check-in sends keys in cleartext (use HTTPS to protect this).</li>
          <li><strong>User-Agent</strong> — Customize to match the target organization's browser profile. Default is Chrome on Windows 10.</li>
          <li><strong>Safe builds</strong> — Deploy safe-build agents first for persistence. Upgrade to offensive builds only when needed on excluded machines.</li>
        </ul>
      </Section>

      {/* WEBSOCKET AGENT */}
      <Section title="WebSocket Agent vs HTTP Beacon" icon={faNetworkWired}>
        <p>PurpleHQ supports two agent communication modes:</p>
        <div className={`mt-2 rounded overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <th className="px-3 py-1.5 text-left">Feature</th>
                <th className="px-3 py-1.5 text-left">HTTP Beacon</th>
                <th className="px-3 py-1.5 text-left">WebSocket Agent</th>
              </tr>
            </thead>
            <tbody className={darkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}>
              <tr><td className="px-3 py-1 font-medium">Connection</td><td className="px-3 py-1">Poll-based (sleep interval)</td><td className="px-3 py-1">Persistent connection</td></tr>
              <tr><td className="px-3 py-1 font-medium">Latency</td><td className="px-3 py-1">Up to sleep interval</td><td className="px-3 py-1">Real-time</td></tr>
              <tr><td className="px-3 py-1 font-medium">Stealth</td><td className="px-3 py-1">Better (intermittent traffic)</td><td className="px-3 py-1">Persistent conn is more visible</td></tr>
              <tr><td className="px-3 py-1 font-medium">HTTP Relay</td><td className="px-3 py-1">No</td><td className="px-3 py-1">Yes — proxy traffic through agent</td></tr>
              <tr><td className="px-3 py-1 font-medium">Failover</td><td className="px-3 py-1">Multi-URL with strategies</td><td className="px-3 py-1">Auto-reconnect</td></tr>
              <tr><td className="px-3 py-1 font-medium">Entry point</td><td className="px-3 py-1"><Code>cmd/agent/main.go</Code></td><td className="px-3 py-1"><Code>main.go</Code> (root)</td></tr>
            </tbody>
          </table>
        </div>
        <p className="mt-2">The <strong>HTTP Beacon</strong> is built via the Payloads tab. The <strong>WebSocket Agent</strong> is used for proxy relay (route traffic through agent to internal targets) and is configured via the web target's Agent Routing settings.</p>
      </Section>

      {/* TROUBLESHOOTING */}
      <Section title="Troubleshooting" icon={faClock}>
        <H>Agent not checking in?</H>
        <ul className="list-disc ml-4 space-y-0.5">
          <li>Check that a <strong>listener</strong> is running on the correct port</li>
          <li>Verify the C2 URL in the agent matches the listener address (including protocol and port)</li>
          <li>Check firewall rules — the target must be able to reach the C2/redirector</li>
          <li>If using redirectors, test the redirect chain from the <strong>Redirectors</strong> tab</li>
          <li>Check PM2 logs: <Code>pm2 logs purpleteam-api</Code></li>
        </ul>
        <H>Build failing?</H>
        <ul className="list-disc ml-4 space-y-0.5">
          <li>Go compiler must be installed on the C2 server (<Code>go version</Code>)</li>
          <li>Agent source must be at <Code>/opt/purpleteam/agent/</Code></li>
          <li>Check build output in the Payloads tab — expand failed builds for error details</li>
          <li>Cross-compilation requires <Code>CGO_ENABLED=0</Code> (automatically set)</li>
        </ul>
        <H>Windows Defender blocking the agent?</H>
        <ul className="list-disc ml-4 space-y-0.5">
          <li>Build with <strong>no modules selected</strong> (safe build) — no AV signatures</li>
          <li>For authorized pentests: add exclusion for the agent binary path in Defender settings</li>
          <li>For code-signed agents: obtain an EV code-signing certificate</li>
          <li>Submit to Microsoft MSRC for false-positive analysis (legitimate security tools only)</li>
        </ul>
      </Section>
    </div>
  );
};

export default C2Help;
